/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { AppService, IApiVersionMetadata } from 'app/shared/services/app-service';
import { HelpService } from 'app/shared/services/help-service';
import { apiCall } from 'app/shared/lib/api/api';
import { localSort } from 'app/shared/lib/sorting';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SystemInfoPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected versionMetadata: IApiVersionMetadata = {};
    protected applicationModules: ReadonlyArray<Core.ApplicationModule> | null = null;
    protected releaseNotesHelpTopicUrl?: string;

    private sorting: Sorting = new Sorting();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly appService: AppService,
        private readonly helpService: HelpService,
        ...rest: any[]) {
        super(...rest);
    }

    protected async attached(): Promise<void> {
        this.releaseNotesHelpTopicUrl = await this.helpService.resolveHelpUrl('core.release-notes');
    }

    protected bind(): void {
        this.loadVersionMetadata();
        this.loadApplicationModules();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        const applicationModules: Core.ApplicationModule[] = <Core.ApplicationModule[]>this.applicationModules;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(applicationModules, sorting);
        if(sorting.entries.length == 0) {
            this.loadApplicationModules();
        };
    }

    private setDefaultSort() {
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadVersionMetadata(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.appService.getVersionMetadata();
            this.versionMetadata = response;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadApplicationModules(): Promise<void> {
        try {
            this.state.beginLoad();
            const sorting = new Sorting;
            sorting.entries = [
                {
                    reference: 'type.description',
                    direction: SortDirection.Ascending
                },
                {
                    reference: 'description',
                    direction: SortDirection.Ascending
                }
            ];
            const includes = ['type'];
            const options: StandardLoadOptions = {
                sorting: sorting,
                includeInactive: false
            };
            const response = await this.appService.getApplicationModules(options, includes);
            this.applicationModules = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
